import { Formik, FormikErrors, FormikProps } from 'formik';
import { InputAutocompleteMulti, InputText } from '@innovyze/stylovyze';

import { Grid } from '@mui/material';
import React from 'react';
import { TSensorTypes } from '@Utils/valuemappingFormHelpers';
import { ValuemappingMappingRowList } from '@Components/ValuemappingMappingRowList';
import { ValuemappingProfileFormShape } from '@Types/valuemappingForm.types';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { useSelector } from 'react-redux';

interface ValuemappingProfileFormProps {
	formRef: React.MutableRefObject<FormikProps<ValuemappingProfileFormShape>>; // ? required because submission (save) button is outside of the form
	initialValues: ValuemappingProfileFormShape;
	errors: Partial<FormikErrors<ValuemappingProfileFormShape>>; // ? Workaround because Formik kept removing errors on everychange (forgets errors)
	disabledFields: Record<'sensorIds', boolean>;
	enableReinitialize?: boolean;
}

export const ValuemappingProfileForm: React.FC<
	ValuemappingProfileFormProps
> = ({
	formRef,
	initialValues,
	errors,
	disabledFields,
	enableReinitialize,
}) => {
	const { t } = useGlobalization();

	const {
		valueMappingProfiles: {
			valueMappingDetail: {
				mappingMethods,
				multiselect: {
					sensorIds: { options: sensorIdOptions },
					sensorTypes: { options: sensorTypeOptions },
				},
			},
		},
	} = useSelector(getValue);

	const checkSelectedOptionsHandler =
		(values: string[]) =>
		(option: string): boolean =>
			values.includes(option);

	const renderMenuItems = (): {
		key: string;
		value: string;
		label: string;
	}[] => {
		const translationValues = TSensorTypes(t);
		return sensorTypeOptions.map(el => ({
			...el,
			label: translationValues[el.key]
				? translationValues[el.key]
				: el.key,
		}));
	};

	return (
		<Formik
			innerRef={formRef}
			onSubmit={() => void 0}
			mode="edit"
			enableReinitialize={!!enableReinitialize}
			initialValues={initialValues}>
			{(formikProps: FormikProps<ValuemappingProfileFormShape>) => {
				return (
					<Grid direction="column" spacing={2} container>
						<Grid item xs={3}>
							<InputText
								dataCy="valuemappingProfileField"
								key="valuemappingProfileField"
								fieldAttrs={{
									name: 'profileName',
								}}
								textField={{
									error: !!errors.profileName,
									helperText: errors.profileName,
									required: true,
									label: t('Profile Name'),
									variant: 'outlined',
									color: 'primary',
									autoComplete: 'off',
								}}
							/>
						</Grid>

						<Grid item container spacing={2}>
							<Grid item sm={12} md={6}>
								<InputAutocompleteMulti
									dataCy="multiselectSensorType"
									options={renderMenuItems()}
									fieldAttrs={{ name: 'sensorTypes' }}
									label={t('Sensor of type')}
									error={!!errors.sensorTypes}
									helperText={errors.sensorTypes as string}
									autocompleteProps={{
										getOptionDisabled:
											checkSelectedOptionsHandler(
												formikProps.values.sensorTypes,
											),
									}}
								/>
							</Grid>

							<Grid
								item
								sm={12}
								md={6}
								style={{
									opacity: disabledFields.sensorIds
										? '0.5'
										: '1',
								}}>
								<InputAutocompleteMulti
									dataCy="multiselectSensorId"
									options={sensorIdOptions}
									fieldAttrs={{
										name: 'sensorIds',
										disabled: disabledFields.sensorIds,
									}}
									label={t('Individual sensors')}
									error={!!errors.sensorIds}
									helperText={
										disabledFields.sensorIds
											? 'loading list'
											: (errors.sensorIds as string)
									}
									autocompleteProps={{
										disabled: disabledFields.sensorIds,
										getOptionDisabled:
											checkSelectedOptionsHandler(
												formikProps.values.sensorIds,
											),
									}}
								/>
							</Grid>
						</Grid>
						<Grid item>
							<ValuemappingMappingRowList
								values={formikProps.values.mappings}
								methodOptions={mappingMethods}
								errors={errors}
							/>
						</Grid>
					</Grid>
				);
			}}
		</Formik>
	);
};

export default ValuemappingProfileForm;
